import { Button } from '@heidi/design-system/button';
import { cn } from '@heidi/utils';
import { Heart } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryCommunityTemplateLikeStatus } from '../../data';

interface LikeButtonProps {
  templateId: string;
  likesCount?: number;
  size?: 'sm' | 'md';
  showCount?: boolean;
  className?: string;
  onLikeChange?: (liked: boolean, count: number) => void;
  isAuthenticated: boolean;
  isPending: boolean;
  onLike: (
    liked: boolean,
    onSuccess: (data: { liked: boolean; likes_count: number }) => void
  ) => void;
}

export function LikeButton({
  templateId,
  likesCount: initialLikesCount = 0,
  size = 'md',
  showCount = true,
  className,
  onLikeChange,
  isAuthenticated,
  isPending,
  onLike,
}: LikeButtonProps) {
  const intl = useIntl();
  const { data: likeStatus } = useQueryCommunityTemplateLikeStatus(templateId);
  const [likesCount, setLikesCount] = useState(initialLikesCount);

  const isLiked = likeStatus?.liked ?? false;

  const handleLikeSuccess = (data: { liked: boolean; likes_count: number }) => {
    setLikesCount(data.likes_count);
    onLikeChange?.(data.liked, data.likes_count);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isAuthenticated) {
      // Could show login prompt here
      return;
    }

    onLike(isLiked, handleLikeSuccess);
  };

  const iconSize = size === 'sm' ? 14 : 18;

  return (
    <Button
      variant="ghost"
      size={size}
      flush="start"
      className={cn(
        'gap-1.5 text-text-secondary hover:text-text-primary',
        isLiked && 'text-accent-red hover:text-accent-red',
        className
      )}
      onClick={handleClick}
      disabled={isPending || !isAuthenticated}
      aria-label={
        isLiked
          ? intl.formatMessage({ id: 'gYFII3UAhE', defaultMessage: 'Unlike template' })
          : intl.formatMessage({ id: 'AWTyqS1U9a', defaultMessage: 'Like template' })
      }
    >
      <Heart
        size={iconSize}
        className={cn(
          'transition-all duration-200',
          isLiked && 'fill-current',
          isPending && 'animate-pulse'
        )}
      />
      {showCount && <span className="text-sm tabular-nums">{likesCount}</span>}
    </Button>
  );
}
